import { TypewriterText } from '../hooks/useTypewriter';

export default function Paper() {
  const API = '';

  const abstractText = `learning algorithms assume meaningful input-state, context, relational structure. a continuous stream provides none of this. before a system can learn from time, it must translate time into state. we argue that the central obstacle to embodied continual learning is not inadequate optimization but inadequate translation: credit assignment is ill-posed until temporal history has been rendered into a representation where responsibility is locally computable. we characterize the constraints on temporal translation — causal, bounded, continuous, locally interpretable — and show they admit essentially one solution class among linear, time-invariant, finite-state summaries: exponentially decaying measurements at geometrically spaced timescales. when spaced by the golden ratio to maximize incommensurability, this decomposition provides a minimal temporal language in which the past is present and credit assignment becomes tractable. we instantiate this in the spectral online machine architecture (soma), demonstrating continuous adaptation without catastrophic forgetting, under fixed resources, with no replay buffer and no sequence storage, reaching 1.87 bits per byte under streaming constraints with bounded memory. the architecture satisfies a requirement that theories of consciousness increasingly emphasize: temporal integration must be intrinsic to the system's state, not externalized as retrievable data. a system with translated time is its history; it does not merely have access to it. the failure of continual learning is not a failure of learning rules. it is a failure to give them a language for time.`;

  const bibtex = `@inproceedings{blight2026soma,
  author    = {Blight, James},
  title     = {Time Is All You Need: Temporal Translation and the Credit Assignment Problem},
  booktitle = {Proceedings of the AAAI Symposium Series},
  editor    = {Petrick, Ron and Geib, Christopher},
  volume    = {8},
  number    = {1},
  pages     = {234--240},
  year      = {2026},
  publisher = {AAAI Press},
  address   = {Washington, DC, USA},
  issn      = {2994-4317},
  url       = {https://ojs.aaai.org/index.php/AAAI-SS/article/view/42548}
}`;

  return (
    <div className="min-h-screen bg-black py-12 lg:py-24 px-6 lg:px-8 page-wrapper" data-testid="paper-page">
      <div className="max-w-3xl mx-auto stagger-children">
        {/* Title */}
        <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase mb-4">
          <TypewriterText text={'"time is all you need"'} speed={45} startDelay={150} />
        </h1>
        
        {/* Subtitle */}
        <p className="text-lg text-[#888888] lowercase mb-2">
          temporal translation and the credit assignment problem
        </p>
        
        {/* Author and venue */}
        <p className="text-[#666666] mb-4">
          james blight · <span className="uppercase">AAAI</span> spring symposium series 2026
        </p>

        {/* Published-in badge with proceedings link */}
        <a
          href="https://ojs.aaai.org/index.php/AAAI-SS/article/view/42548"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-[#383f38] px-4 py-2 rounded-lg text-xs lowercase text-[#aaaaaa] hover:text-white hover:border-white transition-all duration-300 mb-12"
          data-testid="proceedings-link"
        >
          <span className="uppercase tracking-wider text-[#666666]">published in</span>
          <span>machine consciousness · aaai sss-26 · pp. 234–240</span>
          <span aria-hidden="true">↗</span>
        </a>

        {/* Plain English Summary */}
        <div className="mb-12 border border-[#272e28] rounded-xl p-6 lg:p-8 gradient-card">
          <h2 className="text-xl font-bold text-white lowercase mb-4">in plain english</h2>
          <div className="space-y-4 text-[#aaaaaa] lowercase leading-relaxed">
            <p>
              for a system to learn from a continuous stream of experience, it needs a way to represent the past in the present. this paper identifies what that representation must look like: exponentially decayed measurements at geometrically spaced timescales, and shows it is essentially the only solution under the relevant constraints.
            </p>
            <p>
              the result is soma: a family of continual learners built on that structure. every soma runtime carries an exact trace bank at geometrically spaced timescales, tied token dictionaries at input and output, and a fixed learned trunk that composes over the retained present. the model's memory is its state — structured, compressed, and always present. the current runtime, v14, is an adaptive trace-branch mixer; earlier runtimes remain downloadable at <a href="/runtimes" className="text-white underline decoration-[#333333] underline-offset-4 hover:decoration-white">/runtimes</a>.
            </p>
          </div>
        </div>

        {/* Abstract */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-white lowercase mb-4">abstract</h2>
          <p className="text-[#888888] lowercase leading-relaxed text-sm lg:text-base">
            {abstractText}
          </p>
        </div>

        {/* Download button */}
        <a
          href={`${API}/api/download/paper.pdf`}
          download="time_is_all_you_need.pdf"
          className="block w-full border border-[#383f38] px-8 py-5 text-white text-lg lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-xl glow-hover text-center mb-3"
          data-testid="download-paper-btn"
        >
          download paper
        </a>
        <p className="text-center text-[#555555] text-sm lowercase mb-16">
          PDF · ~1 MB
        </p>

        {/* Citation */}
        <div>
          <h2 className="text-xl font-bold text-white lowercase mb-4">citation</h2>
          
          {/* LaTeX bibitem */}
          <div className="border border-[#272e28] rounded-xl p-6 font-mono text-sm text-[#888888] gradient-card mb-4">
            <pre className="whitespace-pre-wrap">{`\\bibitem[{Blight(2026)}]{blight:26}
Blight, J. 2026.
\\newblock Time Is All You Need: Temporal Translation and the Credit
  Assignment Problem.
\\newblock In \\emph{Proceedings of the AAAI Symposium Series},
  volume 8(1), 234--240. AAAI Press.
\\newblock \\url{https://ojs.aaai.org/index.php/AAAI-SS/article/view/42548}.`}</pre>
          </div>

          {/* BibTeX */}
          <div className="border border-[#272e28] rounded-xl p-6 font-mono text-sm text-[#888888] gradient-card">
            <pre className="whitespace-pre-wrap">{bibtex}</pre>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(bibtex);
            }}
            className="mt-4 text-[#555555] lowercase hover:text-white transition-all duration-300 text-sm"
            data-testid="copy-citation-btn"
          >
            copy bibtex
          </button>
        </div>
      </div>
    </div>
  );
}
